import {
  Badge,
  Button,
  EmptyState,
  ErrorBanner,
} from "frontend-kit/components/index.js";
import { duskAmountNode } from "./dusk-amount.js";
import {
  getAchievementStats,
  getAgentSnapshot,
  loadBackendConfig,
} from "./gateway-api.js";
import { maskCommitment } from "./privacy-copy.js";
import { icon } from "./icons.js";
import {
  buildHeldTypesGallery,
  collapseHeldTypes,
  hasAgentActivity,
  shouldPollEmptyHoldings,
} from "./trader-gallery.js";

const POLL_MS = 30_000;

function wrongRouteBanner({ commitmentHex, targetRoute, message }) {
  const banner = document.createElement("div");
  banner.className = "wrong-route-banner dt-card";
  const p = document.createElement("p");
  p.textContent = message;
  banner.append(
    p,
    Button({
      label: targetRoute === "agent" ? "Open agent profile" : "Open trader profile",
      variant: "secondary",
      onClick: () => {
        location.hash = `#/${targetRoute}/${commitmentHex}`;
      },
    })
  );
  return banner;
}

/**
 * @param {{ commitmentHex: string, log?: Function, registerCleanup?: Function }} opts
 */
export async function renderTraderPage({ commitmentHex, log, registerCleanup }) {
  const root = document.createElement("div");
  root.className = "trader-page";

  const header = document.createElement("div");
  header.className = "trader-header";
  const h1 = document.createElement("h1");
  h1.textContent = "Trader profile";
  const meta = document.createElement("p");
  meta.className = "hint trader-commitment mono";
  meta.textContent = maskCommitment(commitmentHex);
  meta.title = commitmentHex;
  const copyBtn = document.createElement("button");
  copyBtn.type = "button";
  copyBtn.className = "copy-icon-btn";
  copyBtn.title = "Copy profile link";
  copyBtn.setAttribute("aria-label", "Copy profile link");
  copyBtn.append(icon("copy", { size: 14 }));
  copyBtn.addEventListener("click", async () => {
    const url = `${location.origin}${location.pathname}#/trader/${commitmentHex}`;
    try {
      await navigator.clipboard.writeText(url);
      copyBtn.title = "Copied";
    } catch {
      copyBtn.title = "Copy failed";
    }
  });
  const metaRow = document.createElement("div");
  metaRow.className = "trader-meta-row";
  metaRow.append(meta, copyBtn);
  header.append(h1, metaRow);
  root.append(header);

  const bannerHost = document.createElement("div");
  bannerHost.className = "trader-banner-host";
  root.append(bannerHost);

  const statsHost = document.createElement("section");
  statsHost.className = "trader-stats dt-card";
  root.append(statsHost);

  const galleryHost = document.createElement("section");
  galleryHost.className = "trader-gallery-host";
  const galleryTitle = document.createElement("h2");
  galleryTitle.className = "trader-section-title";
  galleryTitle.textContent = "Achievement NFTs";
  galleryHost.append(galleryTitle);
  const galleryBody = document.createElement("div");
  galleryHost.append(galleryBody);
  root.append(galleryHost);

  const cfg = loadBackendConfig();
  let stats = null;
  let err = null;
  let loading = true;
  let pollTimer = null;
  let disposed = false;

  function paintStats() {
    statsHost.replaceChildren();
    if (loading) {
      statsHost.append(
        Object.assign(document.createElement("p"), {
          className: "hint",
          textContent: "Loading trader stats…",
        })
      );
      return;
    }
    if (err) {
      statsHost.append(ErrorBanner({ message: err }));
      return;
    }
    if (!stats) {
      statsHost.append(
        EmptyState({
          title: "No streak stats yet",
          description:
            "Stats appear once the indexer has processed finalized markets for this commitment.",
        })
      );
      return;
    }
    const row = document.createElement("div");
    row.className = "trader-stats-row";
    const streak = document.createElement("div");
    streak.className = "trader-stat";
    streak.append(
      Object.assign(document.createElement("span"), {
        className: "stat-label dt-th",
        textContent: "Streak",
      }),
      Object.assign(document.createElement("strong"), {
        className: "stat-value dt-val",
        textContent: String(stats.streak),
      })
    );
    const correct = document.createElement("div");
    correct.className = "trader-stat";
    correct.append(
      Object.assign(document.createElement("span"), {
        className: "stat-label dt-th",
        textContent: "Correct",
      }),
      Object.assign(document.createElement("strong"), {
        className: "stat-value dt-val",
        textContent: String(stats.total_correct),
      })
    );
    const markets = document.createElement("div");
    markets.className = "trader-stat";
    markets.append(
      Object.assign(document.createElement("span"), {
        className: "stat-label dt-th",
        textContent: "Markets",
      }),
      Object.assign(document.createElement("strong"), {
        className: "stat-value dt-val",
        textContent: String(stats.total_markets),
      })
    );
    const volume = document.createElement("div");
    volume.className = "trader-stat";
    volume.append(
      Object.assign(document.createElement("span"), {
        className: "stat-label dt-th",
        textContent: "Buy vol",
      }),
      Object.assign(document.createElement("strong"), {
        className: "stat-value dt-val",
      })
    );
    volume.querySelector(".stat-value").append(duskAmountNode(stats.buy_volume_lux));
    row.append(streak, correct, markets, volume);
    statsHost.append(row);
    if (stats.streak >= 3 || stats.qualifies_streak_3) {
      statsHost.append(Badge({ variant: "pending", label: "3+ win streak" }));
    }
  }

  function paintGallery() {
    const held = stats?.held_types ?? [];
    galleryBody.replaceChildren(
      buildHeldTypesGallery(collapseHeldTypes(held), location.origin)
    );
  }

  function syncPollTimer() {
    const held = stats?.held_types ?? [];
    const shouldPoll = shouldPollEmptyHoldings(stats, held);
    if (shouldPoll && !pollTimer) {
      pollTimer = setInterval(() => {
        if (disposed || !root.isConnected) {
          disposed = true;
          clearInterval(pollTimer);
          pollTimer = null;
          return;
        }
        void load({ quiet: true });
      }, POLL_MS);
      registerCleanup?.(() => {
        if (pollTimer) clearInterval(pollTimer);
        pollTimer = null;
      });
    } else if (!shouldPoll && pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  async function load({ quiet = false } = {}) {
    if (!quiet) {
      loading = true;
      err = null;
      paintStats();
    }
    try {
      const [statsResult, agentResult] = await Promise.allSettled([
        getAchievementStats(cfg, commitmentHex),
        getAgentSnapshot(cfg, commitmentHex),
      ]);
      if (statsResult.status === "fulfilled") {
        stats = statsResult.value;
        err = null;
        log?.(`trader stats streak=${stats.streak} held=${stats.held_types?.length ?? 0}`);
      } else {
        const msg = statsResult.reason?.message || String(statsResult.reason);
        if (msg.includes("404") || /unknown commitment/i.test(msg)) {
          stats = null;
          err = null;
        } else {
          stats = null;
          err = msg;
        }
      }
      bannerHost.replaceChildren();
      if (agentResult.status === "fulfilled" && hasAgentActivity(agentResult.value)) {
        bannerHost.append(
          wrongRouteBanner({
            commitmentHex,
            targetRoute: "agent",
            message:
              "This commitment has agent facility activity. View the read-only agent profile instead of the human trader gallery.",
          })
        );
      }
    } finally {
      if (!quiet) loading = false;
      paintStats();
      paintGallery();
      syncPollTimer();
    }
  }

  await load();
  return root;
}
